'use client';
import { useResource } from '@/lib/use-resource';
import type { HorizonResult } from '@/lib/research/horizon-helpers';

interface HorizonICFile {
  asOf: string;
  fromYear: number;
  toYear: number;
  forwardMonths: number;
  symbolsCount: number;
  monthEndsCount: number;
  level: HorizonResult;
  velocity: Record<string, HorizonResult>;
  acceleration: Record<string, HorizonResult>;
}

const fmtIc = (v: number | null) => v == null ? '—' : v.toFixed(3);
const fmtPct = (v: number) => `${(v * 100 >= 0 ? '+' : '')}${(v * 100).toFixed(1)}%`;
const fmtSharpe = (v: number) => v.toFixed(2);

export function HorizonICTable() {
  const { data, loading } = useResource<HorizonICFile>('/api/horizon-ic');

  if (loading) return <div className="text-[12px] text-[#64748b]">Loading horizon IC sweep…</div>;
  if (!data) {
    return (
      <div className="text-[12px] text-[#64748b] leading-relaxed">
        Horizon IC sweep not yet computed. Run{' '}
        <code className="text-amber-400">npx tsx scripts/compute-horizon-ic.ts</code> to populate (~1-2 min).
      </div>
    );
  }

  const rows: Array<{ label: string; r: HorizonResult; group: 'level' | 'velocity' | 'acceleration' }> = [
    { label: 'Level (current composite)', r: data.level, group: 'level' },
    ...Object.entries(data.velocity).map(([k, r]) => ({ label: `Δ ${k}`, r, group: 'velocity' as const })),
    ...Object.entries(data.acceleration).map(([k, r]) => ({ label: `ΔΔ ${k.replace('_', ' / ')}`, r, group: 'acceleration' as const })),
  ];
  // Find the winning row by spread Sharpe (positive only)
  const winner = [...rows].sort((a, b) => b.r.tier10.spreadSharpe - a.r.tier10.spreadSharpe)[0];

  return (
    <div className="space-y-3">
      <p className="text-[12px] text-[#cbd5e1] leading-relaxed">
        Empirical predictive power of each signal vs forward 12-month return. Built from monthly composite
        reconstructions across {data.symbolsCount} stocks × {data.monthEndsCount} month-ends ({data.fromYear}–{data.toYear}).
        Each signal is paired with its forward 12m return; we report Spearman IC and the top-decile vs bottom-decile
        return spread. <strong className="text-white">Higher IC + higher spread Sharpe = stronger signal.</strong>
      </p>
      <details className="text-[11px] text-[#94a3b8] leading-relaxed">
        <summary className="cursor-pointer hover:text-white">
          <strong className="text-white">IC</strong> = <span className="text-[#94a3b8]">Information Coefficient</span> — Spearman rank correlation between signal and forward return. Scale −1 to +1.
        </summary>
        <ul className="mt-2 pl-4 space-y-1">
          <li><strong className="text-emerald-400">IC ≥ 0.10</strong>: exceptional — Renaissance-tier factor.</li>
          <li><strong className="text-amber-400">IC ≈ 0.05</strong>: solid — real strategies are built on signals at this level.</li>
          <li><strong className="text-[#94a3b8]">IC ≈ 0.02-0.03</strong>: marginal but tradeable.</li>
          <li><strong className="text-[#64748b]">IC &lt; 0.02</strong>: noise. Sign is unreliable.</li>
          <li><strong className="text-red-400">IC &lt; 0</strong>: signal is anti-predictive — high values predict low returns.</li>
        </ul>
      </details>

      <div className="overflow-x-auto">
        <table className="w-full text-[11px] border-collapse">
          <thead>
            <tr className="text-[10px] text-[#64748b] uppercase tracking-wide border-b border-[#1e293b]">
              <th className="text-left py-1.5 pr-3">Signal</th>
              <th className="text-right py-1.5 px-2">IC</th>
              <th className="text-right py-1.5 px-2">Top 10%</th>
              <th className="text-right py-1.5 px-2">Bot 10%</th>
              <th className="text-right py-1.5 px-2">Spread</th>
              <th className="text-right py-1.5 px-2">Sp Sharpe</th>
              <th className="text-right py-1.5 px-2">N</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ label, r, group }) => {
              const t = r.tier10;
              const isWinner = r === winner.r;
              const groupColor =
                group === 'level' ? 'text-blue-400' :
                group === 'velocity' ? 'text-amber-400' :
                'text-purple-400';
              const ic = r.ic;
              return (
                <tr key={label} className={`border-b border-[#1e293b] ${isWinner ? 'bg-emerald-500/5' : ''}`}>
                  <td className={`py-1.5 pr-3 font-mono ${groupColor}`}>
                    {label}
                    {isWinner ? <span className="ml-2 text-[9px] text-emerald-400 uppercase tracking-wide">winner</span> : null}
                  </td>
                  <td className={`py-1.5 px-2 text-right tabular-nums ${ic != null && ic > 0 ? 'text-emerald-400' : ic != null && ic < 0 ? 'text-red-400' : 'text-[#64748b]'}`}>
                    {fmtIc(ic)}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums">{fmtPct(t.topMean)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums">{fmtPct(t.bottomMean)}</td>
                  <td className={`py-1.5 px-2 text-right tabular-nums font-semibold ${t.spread > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {fmtPct(t.spread)}
                  </td>
                  <td className={`py-1.5 px-2 text-right tabular-nums ${t.spreadSharpe > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {fmtSharpe(t.spreadSharpe)}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-[#64748b]">{r.observations.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-[#94a3b8] leading-relaxed border-l border-amber-700/40 pl-3 bg-amber-950/10 py-2">
        <strong className="text-white">What the data says.</strong> At monthly cadence, almost every signal is
        roughly noise (IC ≈ 0, spread Sharpe ≈ 0). The model&apos;s predictive power is concentrated at <strong className="text-amber-400">annual</strong>{' '}
        rebalance with the level (where the published 1.75 walk-forward Sharpe lives). Trying to act on intra-month
        Δcomposite doesn&apos;t reliably add return — the 5-day Δ on the live desk surfaces &quot;what&apos;s moving today,&quot;
        not &quot;what will work next year.&quot; Of the windows tested, <strong className="text-emerald-400">acceleration over 3m/3m</strong>{' '}
        is the only signal with positive sign, and even that is small. <strong className="text-white">Implication for the desk:</strong>{' '}
        the level is the bet; the velocity is the timing nuance, not the predictor.
      </div>

      <details className="text-[11px] text-[#64748b]">
        <summary className="cursor-pointer hover:text-white">Caveats</summary>
        <ul className="mt-2 pl-4 space-y-1 leading-relaxed">
          <li>Trailing-only factors. Bloomberg forward-consensus (the post-2024 enhancement) is not in this analysis. Production with forward factors should be at least as strong, especially at annual rebalance where consensus updates align with fiscal year-ends.</li>
          <li>The asymmetric V&lt;0 penalty drives short-horizon mean reversion: as a stock rallies, V drops (gets expensive), composite drops — but the rally continues. So composite drop short-term predicts continued strength, hurting the IC of the level at monthly cadence.</li>
          <li>The {data.symbolsCount}-stock universe is today&apos;s. Dead names from 2017 aren&apos;t included, but at this market cap real failures are rare; the bottom-tier outperformance is recovery dispersion, not survivor failure-removal.</li>
        </ul>
      </details>
    </div>
  );
}
